import { ref } from 'vue';
import { defineStore } from 'pinia';
import axios from 'axios';
import fetchUISessionAPI from '@/lib/fetch-ui-session';
import { UI_SESSION_KEY } from '@/constants';

interface User {
  empCode: string;
  accessDept: string | null;
  accessLevel: number | null;
}

interface State {
  token: string;
  expires: Date | null;
  backgroundWorkerURL: string;
  user: User;
  makingKeepAliveRequest: boolean;
}

const useUISessionStore = defineStore({
  id: UI_SESSION_KEY,
  state: () =>
    ref<State>({
      token: '',
      expires: null,
      backgroundWorkerURL: '',
      user: {
        empCode: '',
        accessDept: '',
        accessLevel: null,
      },
      makingKeepAliveRequest: false,
    }),
  getters: {
    isExpired(state): boolean {
      const currentTime = new Date();
      return state.expires === null || currentTime > state.expires;
    },
  },
  actions: {
    async initStore(): Promise<void> {
      if (this.isExpired) {
        const state = await this.fetchUISession();
        this.updateState(state);
      }
    },

    async forceRefresh(): Promise<void> {
      const state = await this.fetchUISession(true);
      this.updateState(state);
    },

    async fetchUISession(force: boolean = false): Promise<State> {
      const apiState = (await fetchUISessionAPI(force)) as Partial<State>;
      return {
        token: apiState.token ?? '',
        expires: apiState.expires ?? null,
        backgroundWorkerURL: apiState.backgroundWorkerURL ?? '',
        user: apiState.user ?? {
          empCode: '',
          accessDept: '',
          accessLevel: null,
        },
        makingKeepAliveRequest: apiState.makingKeepAliveRequest ?? false,
      };
    },

    updateState(state: Partial<State>): void {
      this.$state = {
        ...this.$state,
        ...state,
        makingKeepAliveRequest: false,
      };
    },

    // KEEP-ALIVE FUNCTION
    keepAlive() {
      if (this.makingKeepAliveRequest) return;
      this.makingKeepAliveRequest = true;

      axios
        .get('SessionKeepAlive.ashx', {
          withCredentials: true,
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
        })
        .finally(() => {
          this.makingKeepAliveRequest = false;
        });

      // Restart session timeout countdown if available
      if (typeof window.StartTimer === 'function') {
        window.StartTimer(0);
      }
    },
  },
  persist: {
    enabled: true,
  },
});

export default useUISessionStore;
